import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

function conditionBadge(condition) {
  if (!condition) return null;
  const map = {
    NEW: { label: "🌟 New", bg: "rgba(59,130,246,0.12)", color: "#1e40af" },
    LIKE_NEW: { label: "✨ Like New", bg: "rgba(124,58,237,0.12)", color: "#6d28d9" },
    GOOD: { label: "👍 Good", bg: "rgba(22,163,74,0.12)", color: "#15803d" },
    FAIR: { label: "⚠️ Fair", bg: "rgba(217,119,6,0.12)", color: "#b45309" },
    POOR: { label: "🔧 Poor", bg: "rgba(220,38,38,0.12)", color: "#dc2626" },
  };
  const c = map[condition];
  if (!c) return null;
  return (
    <span style={{ display: "inline-block", marginLeft: 8, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

export default function ListingDetailPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [offerPrice, setOfferPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("buy");

  const loadListing = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/listings/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) {
        setListing(data);
        const firstAvailable = data.items?.find(i => i.availableQuantity > 0);
        if (firstAvailable) setSelectedItem(firstAvailable);
      }
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadListing(); }, [loadListing]);
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") setLightbox(false); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isMine = listing?.sellerId === user?.id;

  async function handleMessageSeller() {
    try {
      const res = await fetch(`${API_BASE_URL}/conversations`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ sellerId: listing.sellerId, listingId: listing.id }) });
      const data = await res.json();
      if (res.ok) navigate(`/messages/${data.id}`);
      else window.alert(data.error || "Could not start conversation");
    } catch { window.alert("Something went wrong"); }
  }

  async function handleBuyNow() {
    if (!selectedItem) { window.alert("Please select an item"); return; }
    if (!window.confirm(`Buy ${quantity}x ${selectedItem.title} for $${(Number(selectedItem.listedUnitPrice) * quantity * 1.025).toFixed(2)} total?`)) return;
    setSubmitting(true);
    try {
      const subtotal = Number(selectedItem.listedUnitPrice) * quantity;
      const platformFee = subtotal * 0.025;
      const orderRes = await fetch(`${API_BASE_URL}/orders`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ buyerId: user.id, sellerId: listing.sellerId, listingId: listing.id, platformFee, pricingSource: "LISTED_PRICE", items: [{ listingItemId: selectedItem.id, quantity, listedUnitPrice: Number(selectedItem.listedUnitPrice), lockedUnitPrice: Number(selectedItem.listedUnitPrice) }] })
      });
      const orderData = await orderRes.json();
      if (!orderRes.ok) { window.alert(orderData.error || "Could not create order"); return; }
      navigate(`/checkout?orderId=${orderData.id}`);
    } catch { window.alert("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  }

  async function handleMakeOffer() {
    if (!selectedItem) { window.alert("Please select an item"); return; }
    if (!offerPrice || Number(offerPrice) <= 0) { window.alert("Please enter a valid offer price"); return; }
    if (selectedItem.minimumOfferPrice && Number(offerPrice) < Number(selectedItem.minimumOfferPrice)) { window.alert(`Minimum offer is $${Number(selectedItem.minimumOfferPrice).toFixed(2)}`); return; }
    if (!window.confirm(`Submit offer of $${Number(offerPrice).toFixed(2)} per item?`)) return;
    setSubmitting(true);
    try {
      const lineTotal = Number(offerPrice) * quantity;
      const res = await fetch(`${API_BASE_URL}/offers`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ listingId: listing.id, buyerId: user.id, sellerId: listing.sellerId, totalAmount: lineTotal, items: [{ listingItemId: selectedItem.id, quantity, offeredUnitPrice: Number(offerPrice), lineTotal }] })
      });
      const data = await res.json();
      if (!res.ok) { window.alert(data.error || "Could not submit offer"); return; }
      window.alert("Offer submitted! The seller will respond shortly.");
      navigate("/orders");
    } catch { window.alert("Something went wrong."); }
    finally { setSubmitting(false); }
  }

  if (loading) return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
      Loading...
    </div>
  );

  if (!listing) return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
      Listing not found
    </div>
  );

  const photos = selectedItem?.photoUrls?.length > 0
    ? selectedItem.photoUrls
    : listing.items?.find(i => i.photoUrls?.length > 0)?.photoUrls || [];

  const subtotal = selectedItem ? Number(selectedItem.listedUnitPrice) * quantity : 0;
  const platformFee = subtotal * 0.025;
  const total = subtotal + platformFee;
  const offerSubtotal = offerPrice ? Number(offerPrice) * quantity : 0;
  const offerFee = offerSubtotal * 0.025;
  const offerTotal = offerSubtotal + offerFee;

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 1200, margin: "0 auto", padding: "32px 20px" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 24, padding: 0, display: "flex", alignItems: "center", gap: 6 },
    layout: { display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 48, alignItems: "start" },
    photoSection: { position: "sticky", top: 90 },
    mainPhotoWrap: { width: "100%", aspectRatio: "1", borderRadius: 20, overflow: "hidden", marginBottom: 12, background: "rgba(255,255,255,0.7)", border: "1px solid rgba(59,130,246,0.12)", cursor: photos.length > 0 ? "zoom-in" : "default", boxShadow: "0 4px 20px rgba(59,130,246,0.1)" },
    mainPhoto: { width: "100%", height: "100%", objectFit: "cover" },
    photoPlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80, background: "linear-gradient(135deg, rgba(59,130,246,0.06), rgba(124,58,237,0.06))" },
    thumbRow: { display: "flex", gap: 10, flexWrap: "wrap" },
    thumb: (active) => ({ width: 80, height: 80, objectFit: "cover", borderRadius: 12, cursor: "pointer", border: active ? "3px solid #3b82f6" : "3px solid transparent", opacity: active ? 1 : 0.55, transition: "all 0.2s" }),
    sellerRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
    sellerAvatar: { width: 36, height: 36, borderRadius: "50%", background: "rgba(59,130,246,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#3b82f6" },
    sellerName: { fontSize: 14, fontWeight: 600, color: "#475569" },
    listingTitle: { fontSize: 28, fontWeight: 800, marginBottom: 12, lineHeight: 1.2, color: "#0f172a" },
    priceRow: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 },
    price: { fontSize: 36, fontWeight: 800, color: "#1e40af" },
    priceLabel: { fontSize: 14, color: "#64748b" },
    availBadge: (ok) => ({ display: "inline-block", padding: "4px 12px", borderRadius: 999, background: ok ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.1)", color: ok ? "#16a34a" : "#dc2626", fontSize: 12, fontWeight: 700 }),
    badgeRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 20 },
    description: { fontSize: 14, color: "#475569", lineHeight: 1.8, marginBottom: 24, padding: 16, background: "rgba(255,255,255,0.6)", borderRadius: 12, border: "1px solid rgba(59,130,246,0.08)" },
    itemsLabel: { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 },
    itemCard: (active) => ({ padding: "12px 16px", borderRadius: 12, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.12)", background: active ? "rgba(59,130,246,0.06)" : "rgba(255,255,255,0.6)", cursor: "pointer", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }),
    itemName: { fontSize: 14, fontWeight: 700, marginBottom: 2, color: "#1e293b" },
    itemAvail: { fontSize: 12, color: "#64748b" },
    itemPrice: { fontSize: 18, fontWeight: 800, color: "#1e40af" },
    divider: { height: 1, background: "rgba(59,130,246,0.1)", margin: "20px 0" },
    tabs: { display: "flex", gap: 6, marginBottom: 16 },
    tabBtn: (active) => ({ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.15)", background: active ? "#3b82f6" : "rgba(255,255,255,0.7)", color: active ? "#fff" : "#475569", cursor: "pointer", fontWeight: 700, fontSize: 14, textAlign: "center" }),
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 15, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    qtyRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
    qtyBtn: { width: 40, height: 40, borderRadius: 10, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", color: "#1e40af", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 },
    qtyNum: { fontSize: 20, fontWeight: 800, minWidth: 30, textAlign: "center", color: "#0f172a" },
    summaryBox: { background: "rgba(255,255,255,0.7)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: 16, marginBottom: 16, boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    summaryRow: { display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8, color: "#64748b" },
    summaryTotal: { display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 800, paddingTop: 10, borderTop: "1px solid rgba(59,130,246,0.1)", color: "#0f172a" },
    buyBtn: { width: "100%", background: "#3b82f6", color: "#fff", border: "none", padding: "16px", borderRadius: 14, cursor: "pointer", fontWeight: 800, fontSize: 17, marginBottom: 10, opacity: submitting ? 0.5 : 1, boxShadow: "0 4px 16px rgba(59,130,246,0.35)" },
    offerBtn: { width: "100%", background: "transparent", color: "#1e40af", border: "2px solid rgba(59,130,246,0.3)", padding: "14px", borderRadius: 14, cursor: "pointer", fontWeight: 700, fontSize: 15, opacity: submitting ? 0.5 : 1 },
    msgBtn: { width: "100%", background: "rgba(255,255,255,0.7)", color: "#475569", border: "1px solid rgba(59,130,246,0.15)", padding: "12px", borderRadius: 12, cursor: "pointer", fontWeight: 600, fontSize: 14, marginBottom: 12, marginTop: 10 },
    protectionBox: { display: "flex", gap: 10, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 12, padding: "12px 14px", marginTop: 14 },
    protectionText: { fontSize: 12, color: "#15803d", lineHeight: 1.5 },
    manageBanner: { background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 14, padding: "14px 18px", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" },
    manageBtn: { background: "#7c3aed", color: "#fff", border: "none", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
  };

  return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={styles.main}>
        <button style={styles.backBtn} onClick={() => navigate("/home")}>← Back to Marketplace</button>

        {isMine && (
          <div style={styles.manageBanner}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#6d28d9" }}>✏️ This is your listing</span>
            <button style={styles.manageBtn} onClick={() => navigate(`/listing/${id}`)}>Edit Listing</button>
          </div>
        )}

        <div style={styles.layout}>
          <div style={styles.photoSection}>
            <div style={styles.mainPhotoWrap} onClick={() => photos.length > 0 && setLightbox(true)}>
              {photos.length > 0 ? (
                <img src={photos[selectedPhoto]} alt={selectedItem?.title || listing.title} style={styles.mainPhoto} onError={e => { e.target.style.display = "none"; }} />
              ) : (
                <div style={styles.photoPlaceholder}>🛍️</div>
              )}
            </div>
            {photos.length > 1 && (
              <div style={styles.thumbRow}>
                {photos.map((url, i) => (
                  <img key={i} src={url} alt={`Photo ${i + 1}`} style={styles.thumb(selectedPhoto === i)} onClick={() => setSelectedPhoto(i)} onError={e => { e.target.style.display = "none"; }} />
                ))}
              </div>
            )}
            {photos.length > 0 && <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", marginTop: 8 }}>🔍 Click photo to enlarge</div>}
          </div>

          <div>
            <div style={styles.sellerRow}>
              <div style={styles.sellerAvatar}>{listing.seller?.fullName?.charAt(0)}</div>
              <div style={styles.sellerName}>{listing.seller?.fullName}</div>
            </div>
            <div style={styles.listingTitle}>{listing.title}</div>

            {selectedItem && (
              <>
                <div style={styles.priceRow}>
                  <div style={styles.price}>${Number(selectedItem.listedUnitPrice).toFixed(2)}</div>
                  {selectedItem.isNegotiable && <div style={styles.priceLabel}>· Offers accepted</div>}
                </div>
                <div style={styles.badgeRow}>
                  <div style={styles.availBadge(selectedItem.availableQuantity > 0)}>
                    {selectedItem.availableQuantity > 0 ? `✓ ${selectedItem.availableQuantity} available` : "Out of stock"}
                  </div>
                  {conditionBadge(selectedItem.condition)}
                </div>
              </>
            )}

            {listing.description && <div style={styles.description}>{listing.description}</div>}

            {listing.items?.length > 1 && (
              <div style={{ marginBottom: 20 }}>
                <div style={styles.itemsLabel}>Select Item</div>
                {listing.items.map(item => (
                  <div key={item.id} style={styles.itemCard(selectedItem?.id === item.id)} onClick={() => { setSelectedItem(item); setSelectedPhoto(0); setQuantity(1); }}>
                    <div>
                      <div style={styles.itemName}>{item.title}</div>
                      <div style={styles.itemAvail}>{item.availableQuantity > 0 ? `${item.availableQuantity} available` : "Out of stock"}{item.isNegotiable && " · Offers OK"}{item.condition && ` · ${item.condition.replace("_", " ")}`}</div>
                    </div>
                    <div style={styles.itemPrice}>${Number(item.listedUnitPrice).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}

            {!isMine && selectedItem && selectedItem.availableQuantity > 0 && (
              <>
                <div style={styles.divider} />
                {selectedItem.isNegotiable && (
                  <div style={styles.tabs}>
                    <div style={styles.tabBtn(tab === "buy")} onClick={() => setTab("buy")}>Buy Now</div>
                    <div style={styles.tabBtn(tab === "offer")} onClick={() => setTab("offer")}>Make Offer</div>
                  </div>
                )}
                <label style={styles.label}>Quantity</label>
                <div style={styles.qtyRow}>
                  <button style={styles.qtyBtn} onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                  <div style={styles.qtyNum}>{quantity}</div>
                  <button style={styles.qtyBtn} onClick={() => setQuantity(Math.min(selectedItem.availableQuantity, quantity + 1))}>+</button>
                  <span style={{ fontSize: 13, color: "#64748b" }}>of {selectedItem.availableQuantity} available</span>
                </div>
                {tab === "offer" && (
                  <>
                    <label style={styles.label}>Your Offer (per item){selectedItem.minimumOfferPrice && ` · Min $${Number(selectedItem.minimumOfferPrice).toFixed(2)}`}</label>
                    <input style={styles.input} type="number" step="0.01" placeholder="Enter your offer price" value={offerPrice} onChange={e => setOfferPrice(e.target.value)} />
                  </>
                )}
                <div style={styles.summaryBox}>
                  <div style={styles.summaryRow}><span>Item price × {quantity}</span><span>${tab === "offer" && offerPrice ? offerSubtotal.toFixed(2) : subtotal.toFixed(2)}</span></div>
                  <div style={styles.summaryRow}><span>Platform fee (2.5%)</span><span>${tab === "offer" && offerPrice ? offerFee.toFixed(2) : platformFee.toFixed(2)}</span></div>
                  <div style={styles.summaryTotal}><span>Total</span><span style={{ color: "#1e40af" }}>${tab === "offer" && offerPrice ? offerTotal.toFixed(2) : total.toFixed(2)}</span></div>
                </div>
                {tab === "buy" ? (
                  <button style={styles.buyBtn} onClick={handleBuyNow} disabled={submitting}>{submitting ? "Processing..." : `Buy Now · $${total.toFixed(2)}`}</button>
                ) : (
                  <button style={styles.offerBtn} onClick={handleMakeOffer} disabled={submitting}>{submitting ? "Submitting..." : `Submit Offer · $${offerTotal.toFixed(2)}`}</button>
                )}
                <button style={styles.msgBtn} onClick={handleMessageSeller}>💬 Message Seller</button>
                <div style={styles.protectionBox}>
                  <span style={{ fontSize: 18 }}>🔒</span>
                  <div style={styles.protectionText}><strong style={{ color: "#15803d" }}>Buyer Protected</strong> — Seller only receives payment after you confirm delivery.</div>
                </div>
              </>
            )}

            {!isMine && selectedItem && selectedItem.availableQuantity === 0 && (
              <>
                <div style={{ background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.15)", borderRadius: 12, padding: 16, color: "#dc2626", fontSize: 14, marginBottom: 12 }}>This item is currently out of stock.</div>
                <button style={styles.msgBtn} onClick={handleMessageSeller}>💬 Message Seller</button>
              </>
            )}
          </div>
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.95)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, padding: 20 }}>
          <img src={photos[selectedPhoto]} alt="Full size" onClick={e => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain", borderRadius: 12, boxShadow: "0 0 60px rgba(0,0,0,0.8)" }} />
          {photos.length > 1 && (
            <div style={{ display: "flex", gap: 10 }} onClick={e => e.stopPropagation()}>
              {photos.map((url, i) => (
                <img key={i} src={url} alt={`Photo ${i + 1}`} onClick={() => setSelectedPhoto(i)} style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, cursor: "pointer", border: i === selectedPhoto ? "3px solid #3b82f6" : "3px solid transparent", opacity: i === selectedPhoto ? 1 : 0.5, transition: "all 0.2s" }} />
              ))}
            </div>
          )}
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>Click anywhere or press Esc to close</div>
        </div>
      )}
    </div>
  );
}